import { LocalizationString } from '@celito.clients/assets';
import {
  ButtonTypes,
  ObjectEnum,
  OperatorsEnum,
  SortOrderEnum,
} from '@celito.clients/enums';
import { CustomButton, Icon, InHouseInputSelect } from '@celito.clients/shared';
import { useTheme } from '@celito.clients/theme';
import {
  createTestAttribute,
  findDataTestIdFromField,
  getReferencePickerType,
} from '@celito.clients/utils';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
} from '@fluentui/react-components';
import { IChangeData } from 'libs/shared/src/lib/in-house-input-select/in-house-input-select.model';
import { components, OptionProps } from 'react-select';

import { CourseInitiationTypeEnum } from '../../object-initiation-screen.model';
import { CoursePickerModalProps } from './course-picker-modal.model';
import { courseModalStyles } from './course-picker-modal-styles';

interface CoursePickerModalViewProps extends CoursePickerModalProps {
  selectedValue: IChangeData | undefined;
  onPickerValChange: (val: IChangeData) => void;
  version: string | undefined;
  setVersion: (val: string | undefined) => void;
  onClose: () => void;
}

export const Option = (props: OptionProps) => {
  const dataTestId = findDataTestIdFromField(props.label);

  return (
    <components.Option {...props}>
      <span
        data-testid={`dropdown-reference-document-${createTestAttribute(
          dataTestId
        )}`}
      >
        {props.label}
      </span>
    </components.Option>
  );
};

export const CoursePickerModalView: React.FC<CoursePickerModalViewProps> = ({
  courseUpdatePickerModalOpen,
  onCloseCourseUpdatePickerModal,
  selectedValue,
  onPickerValChange,
  version,
  setVersion,
  onUpdateInitiate,
  onClose,
}) => {
  const { cssVariables } = useTheme();

  const styles = courseModalStyles();

  return (
    <Dialog open={courseUpdatePickerModalOpen}>
      <DialogSurface
        className={styles.container}
        style={{ padding: 0, ...cssVariables }}
      >
        <DialogBody className={styles.dialogBody}>
          <DialogTitle className={styles.modalHeader}>
            Update Course
            <Button
              appearance="subtle"
              icon={<Icon iconName="Dismiss24Regular" />}
              onClick={onCloseCourseUpdatePickerModal}
            />
          </DialogTitle>

          <DialogContent className={styles.modalBody}>
            <div className={styles.referencePickerSize}>
              <InHouseInputSelect
                multiselect={false}
                label="Course"
                referencePicker={{
                  objectName: ObjectEnum.COURSE,
                  filters: {
                    conditions: {
                      all: [
                        {
                          all: [
                            {
                              attribute: 'version',
                              operator: OperatorsEnum.ENDS_WITH,
                              value: '.0',
                            },
                            {
                              attribute: 'isActive',
                              operator: OperatorsEnum.EQUALS,
                              value: true,
                            },
                            {
                              attribute: 'status',
                              operator: OperatorsEnum.EQUALS,
                              value: 'Approved',
                            },
                            {
                              attribute: 'activeFlowName',
                              operator: OperatorsEnum.IS_BLANK,
                              value: null,
                            },
                            {
                              attribute: 'course_type__a',
                              operator: OperatorsEnum.NOT_EQUALS,
                              value: 'CD',
                            },
                          ],
                        },
                      ],
                    },
                  },
                  sortConfig: {
                    attribute: 'label',
                    order: SortOrderEnum.ASC,
                  },
                  pickerType: getReferencePickerType('course__a'),
                }}
                onOptionSelect={(_, data) => {
                  const version = data?.data?.version;
                  if (version) setVersion(version as string);
                  onPickerValChange(data);
                }}
                options={[]}
                selectedOptions={selectedValue?.selectedOptions}
                fieldClassName={styles.fieldContainer}
              />
            </div>
          </DialogContent>

          <DialogActions className={styles.modalFooter}>
            <CustomButton
              buttonType={ButtonTypes.Ghost}
              buttonTitle={LocalizationString.CANCEL}
              onClick={onClose}
            />

            <CustomButton
              buttonType={ButtonTypes.Primary}
              buttonTitle={'Update'}
              isDisabled={selectedValue === undefined}
              onClick={() =>
                onUpdateInitiate({
                  ...selectedValue?.data,
                  version: version!,
                  objectDefinitionName: ObjectEnum.COURSE,
                  requestType: CourseInitiationTypeEnum.UpdateCourse,
                  editViewName: 'course_edit_view__a',
                })
              }
            />
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
